import React, { useEffect, useMemo, useState } from "react";
import "../sass/pages/loginScreen.scss";
import logo from "../assets/logo-longo-escuro.webp";
import axios from "axios";
import {
  FiActivity,
  FiAlertTriangle,
  FiBarChart2,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiEye,
  FiEyeOff,
  FiLock,
  FiRadio,
  FiServer,
  FiShield,
  FiTrendingUp,
  FiUser,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { showError } from "../utils/toast";
import { setAuthTokens } from "../auth/authStorage";

const RAW_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  "https://apisantsystemweb30git-production-318e.up.railway.app";

const baseURL = `${String(RAW_BASE).replace(/\/+$/, "")}/ssw`;

const http = axios.create({
  baseURL,
  timeout: 30000,
});

http.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.code === "ERR_NETWORK" || err.message === "Network Error" || !err.response) {
      err.isNetwork = true;
    }
    return Promise.reject(err);
  }
);

const DASHBOARD_LOGIN_FALLBACK = {
  success: true,
  meta: {
    source: "dashboard-login-fallback",
    generatedAt: null,
    fromCache: true,
    cacheTtlSeconds: 180,
  },
  kpis: {
    clientesAtivos: 0,
    chamadosHoje: 0,
    chamadosEmFilaHoje: 0,
    chamadosEmAtendimentoHoje: 0,
    chamadosFinalizadosHoje: 0,
    projetosAtivos: 0,
    projetosAtrasados: 0,
    releasesEmTeste: 0,
  },
  chamados: {
    hoje: {
      total: 0,
      emFila: 0,
      emAtendimento: 0,
      finalizados: 0,
    },
    ultimos7Dias: [
      { bucket: "D-6", label: "D-6", resolvidos: 0, pendentes: 0, total: 0 },
      { bucket: "D-5", label: "D-5", resolvidos: 0, pendentes: 0, total: 0 },
      { bucket: "D-4", label: "D-4", resolvidos: 0, pendentes: 0, total: 0 },
      { bucket: "D-3", label: "D-3", resolvidos: 0, pendentes: 0, total: 0 },
      { bucket: "D-2", label: "D-2", resolvidos: 0, pendentes: 0, total: 0 },
      { bucket: "D-1", label: "D-1", resolvidos: 0, pendentes: 0, total: 0 },
      { bucket: "HOJE", label: "Hoje", resolvidos: 0, pendentes: 0, total: 0 },
    ],
  },
  projetos: {
    totais: {
      ativos: 0,
      atrasados: 0,
      emTeste: 0,
    },
    porStatus: [],
  },
};

const normalizarFlagSN = (valor, padrao = "S") => {
  const v = String(valor ?? "").trim().toUpperCase();
  if (v === "N") return "N";
  if (v === "S") return "S";
  return padrao;
};

const numero = (valor) => Number(valor || 0);

const formatarNumero = (valor) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(numero(valor));

const normalizarDashboardLogin = (payload) => {
  const data = payload?.success ? payload : DASHBOARD_LOGIN_FALLBACK;
  const kpis = data?.kpis || {};
  const chamadosHoje = data?.chamados?.hoje || {};
  const projetosTotais = data?.projetos?.totais || {};

  return {
    ...DASHBOARD_LOGIN_FALLBACK,
    ...data,
    meta: {
      ...DASHBOARD_LOGIN_FALLBACK.meta,
      ...(data?.meta || {}),
    },
    kpis: {
      clientesAtivos: numero(kpis.clientesAtivos),
      chamadosHoje: numero(kpis.chamadosHoje),
      chamadosEmFilaHoje: numero(kpis.chamadosEmFilaHoje),
      chamadosEmAtendimentoHoje: numero(kpis.chamadosEmAtendimentoHoje),
      chamadosFinalizadosHoje: numero(kpis.chamadosFinalizadosHoje),
      projetosAtivos: numero(kpis.projetosAtivos),
      projetosAtrasados: numero(kpis.projetosAtrasados),
      releasesEmTeste: numero(kpis.releasesEmTeste),
    },
    chamados: {
      hoje: {
        total: numero(chamadosHoje.total),
        emFila: numero(chamadosHoje.emFila),
        emAtendimento: numero(chamadosHoje.emAtendimento),
        finalizados: numero(chamadosHoje.finalizados),
      },
      ultimos7Dias:
        Array.isArray(data?.chamados?.ultimos7Dias) && data.chamados.ultimos7Dias.length
          ? data.chamados.ultimos7Dias.map((item) => ({
              bucket: item.bucket,
              label: item.label,
              resolvidos: numero(item.resolvidos),
              pendentes: numero(item.pendentes),
              total: numero(item.total ?? numero(item.resolvidos) + numero(item.pendentes)),
            }))
          : DASHBOARD_LOGIN_FALLBACK.chamados.ultimos7Dias,
    },
    projetos: {
      totais: {
        ativos: numero(projetosTotais.ativos),
        atrasados: numero(projetosTotais.atrasados),
        emTeste: numero(projetosTotais.emTeste),
      },
      porStatus: Array.isArray(data?.projetos?.porStatus) ? data.projetos.porStatus : [],
    },
  };
};

const gravarSessaoLogin = ({ respData, usuarioLista, usuarioSelecionado }) => {
  const {
    IdFuncionario,
    codFunc,
    token,
    refreshToken,
    setor,
    cargo,
    codCad,
    Nivel,
    NivelParametro,
    dmControlaPonto,
    dmExibeLembretePonto,
  } = respData || {};

  const codFuncFinal = codFunc || usuarioSelecionado;
  const usuarioLogado = usuarioLista.find(
    (u) => String(u.codFunc) === String(codFuncFinal)
  );

  const idFuncionarioFinal = Number(IdFuncionario ?? usuarioLogado?.IdFuncionario ?? 0);
  const nomeFinal = respData?.usuario || usuarioLogado?.nome || "";
  const controlaPontoFinal = normalizarFlagSN(dmControlaPonto, "S");
  const exibeLembretePontoFinal = normalizarFlagSN(dmExibeLembretePonto, "S");

  const nivelParametroNumero = Number(NivelParametro ?? 0);
  const nivelParametroFinal =
    Number.isInteger(nivelParametroNumero) &&
    nivelParametroNumero >= 0 &&
    nivelParametroNumero <= 5
      ? nivelParametroNumero
      : 0;

  setAuthTokens({ token, refreshToken });

  sessionStorage.setItem("Setor", setor || "");
  sessionStorage.setItem("Cargo", cargo || "");
  sessionStorage.setItem("codFunc", String(codFuncFinal || ""));
  sessionStorage.setItem("NivelFunc", Nivel || "");
  sessionStorage.setItem("NivelParametro", String(nivelParametroFinal));
  sessionStorage.setItem("nivelParametro", String(nivelParametroFinal));
  sessionStorage.setItem("IdFuncionario", idFuncionarioFinal ? String(idFuncionarioFinal) : "");
  sessionStorage.setItem("DevId", idFuncionarioFinal ? String(idFuncionarioFinal) : "");
  sessionStorage.setItem("CodCadFunc", String(codCad || ""));
  sessionStorage.setItem("RazaoSocial", nomeFinal);

  sessionStorage.setItem("dmControlaPonto", controlaPontoFinal);
  sessionStorage.setItem("dmExibeLembretePonto", exibeLembretePontoFinal);
  sessionStorage.setItem("DM_CONTROLA_PONTO", controlaPontoFinal);
  sessionStorage.setItem("DM_EXIBE_LEMBRETE_PONTO", exibeLembretePontoFinal);

  sessionStorage.setItem(
    "user",
    JSON.stringify({
      IdFuncionario: idFuncionarioFinal || null,
      codFunc: Number(codFuncFinal),
      nome: nomeFinal,
      setor,
      cargo,
      codCad,
      Nivel,
      NivelParametro: nivelParametroFinal,
      nivelParametro: nivelParametroFinal,
      dmControlaPonto: controlaPontoFinal,
      dmExibeLembretePonto: exibeLembretePontoFinal,
    })
  );

  localStorage.setItem("ultimoUsuario", String(codFuncFinal || ""));
};

const MiniMetric = ({ icon, label, value, detail, tone = "blue" }) => (
  <div className={`login-command-metric login-command-metric-${tone}`}>
    <div className="login-command-metric-icon">{icon}</div>
    <div className="login-command-metric-copy">
      <span>{label}</span>
      <strong>{formatarNumero(value)}</strong>
      {detail && <small>{detail}</small>}
    </div>
  </div>
);

const LoginScreen = () => {
  const navigate = useNavigate();

  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(true);
  const [dashboardLogin, setDashboardLogin] = useState(DASHBOARD_LOGIN_FALLBACK);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [dashboardErro, setDashboardErro] = useState(false);

  const usuarioAtual = useMemo(
    () => usuarios.find((u) => String(u.codFunc) === String(usuarioSelecionado)),
    [usuarios, usuarioSelecionado]
  );

  const timelineDashboard = useMemo(() => {
    const lista = dashboardLogin?.chamados?.ultimos7Dias || [];
    const ultimos = lista.slice(-7);
    const maxTotal = Math.max(...ultimos.map((item) => numero(item.total)), 1);

    return ultimos.map((item) => {
      const total = numero(item.total);
      const resolvidos = numero(item.resolvidos);
      const pendentes = numero(item.pendentes);
      const percentual = Math.max(total > 0 ? 12 : 3, Math.round((total / maxTotal) * 100));
      const resolvidosPercentual =
        total > 0 ? Math.max(0, Math.round((resolvidos / total) * 100)) : 0;

      return {
        ...item,
        total,
        resolvidos,
        pendentes,
        percentual,
        resolvidosPercentual,
      };
    });
  }, [dashboardLogin]);

  const taxaFinalizacaoHoje = useMemo(() => {
    const total = numero(dashboardLogin?.chamados?.hoje?.total);
    const finalizados = numero(dashboardLogin?.chamados?.hoje?.finalizados);
    if (!total) return 0;
    return Math.round((finalizados / total) * 100);
  }, [dashboardLogin]);

  const statusDashboard = useMemo(() => {
    if (loadingDashboard) return "Sincronizando";
    if (dashboardErro) return "Fallback seguro";
    if (dashboardLogin?.meta?.fromCache) return "Cache ativo";
    return "Atualizado agora";
  }, [dashboardErro, dashboardLogin?.meta?.fromCache, loadingDashboard]);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      setLoadingUsuarios(true);
      try {
        const resp = await http.get("/loginUser");
        const data = (Array.isArray(resp.data) ? resp.data : []).map((u) => ({
          IdFuncionario: u.IdFuncionario ?? u.idFuncionario,
          codFunc: u.codFunc ?? u.CodFunc ?? u.codfunc,
          codCad: u.codCad ?? u.CodCad,
          nome: u.nome ?? u.Nome,
          nivel: u.nivel ?? u.Nivel,
        }));

        if (cancelado) return;

        setUsuarios(data);

        const ultimo = localStorage.getItem("ultimoUsuario");
        const existeUltimo = data.some((u) => String(u.codFunc) === String(ultimo));
        if (ultimo && existeUltimo) setUsuarioSelecionado(ultimo);
      } catch (err) {
        console.error("[loginUser] falha:", err?.message || err);
        showError("Falha ao carregar usuários.");
      } finally {
        if (!cancelado) setLoadingUsuarios(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    let cancelado = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 3500);

    (async () => {
      setLoadingDashboard(true);
      try {
        const resp = await http.get("/dashboard-login", {
          signal: controller.signal,
        });

        if (cancelado) return;

        setDashboardLogin(normalizarDashboardLogin(resp.data));
        setDashboardErro(false);
      } catch (err) {
        if (cancelado) return;

        console.warn("[dashboard-login] usando fallback:", err?.message || err);
        setDashboardLogin(DASHBOARD_LOGIN_FALLBACK);
        setDashboardErro(true);
      } finally {
        window.clearTimeout(timeoutId);
        if (!cancelado) setLoadingDashboard(false);
      }
    })();

    return () => {
      cancelado = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!usuarioSelecionado) {
      showError("Selecione um usuário.");
      return;
    }

    if (!String(senha || "").trim()) {
      showError("Informe a senha.");
      return;
    }

    try {
      setLoading(true);

      const resp = await http.post("/login", {
        codFunc: Number(usuarioSelecionado),
        senha: String(senha ?? "").trim(),
      });

      if (!resp.data?.sucesso) {
        showError(resp.data?.message || "Credenciais inválidas.");
        return;
      }

      gravarSessaoLogin({
        respData: resp.data,
        usuarioLista: usuarios,
        usuarioSelecionado,
      });

      navigate("/chamados");
    } catch (err) {
      console.error("[login] falha:", err?.response?.data || err);

      if (err.isNetwork) {
        showError("Não foi possível conectar ao servidor.");
      } else if (err.response?.status === 401) {
        showError("Senha inválida.");
      } else if (err.response?.status === 403) {
        showError(err.response?.data?.message || "Usuário sem permissão ou inativo.");
      } else if (err.response?.status === 404) {
        showError("Usuário não encontrado/inativo.");
      } else {
        showError(`Erro no login: ${err?.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
      setSenha("");
    }
  };

  return (
    <div className="login-container">
      <div className="login-bg-grid" />
      <div className="login-bg-orb login-bg-orb-one" />
      <div className="login-bg-orb login-bg-orb-two" />

      <main className="login-shell" aria-label="Login SantSystem Web 3.0">
        <section className="login-access-panel">
          <div className="login-access-top">
            {logo && <img src={logo} alt="SantSystem" className="login-logo" />}
            <span className="login-version">Web 3.0</span>
          </div>

          <div className="login-title-block">
            <span className="login-eyebrow">
              <i />
              Acesso corporativo
            </span>
            <h1>Entre no centro operacional</h1>
            <p>
              Autenticação segura para acessar filas, chamados, projetos e rotinas internas.
            </p>
          </div>

          <div className="login-form-card">
            <form onSubmit={handleLogin} className="login-form">
              <label className="login-field">
                <span>Usuário</span>
                <div className="login-input-wrap">
                  <FiUser className="login-input-icon" />
                  <select
                    value={usuarioSelecionado}
                    onChange={(e) => setUsuarioSelecionado(e.target.value)}
                    required
                    autoComplete="username"
                    disabled={loadingUsuarios || loading}
                  >
                    <option value="">
                      {loadingUsuarios ? "Carregando usuários..." : "Selecione o usuário"}
                    </option>
                    {usuarios.map((u) => (
                      <option key={u.codFunc} value={u.codFunc}>
                        {u.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label className="login-field">
                <span>Senha</span>
                <div className="login-input-wrap campo-senha">
                  <FiLock className="login-input-icon" />
                  <input
                    type={mostrarSenha ? "text" : "password"}
                    placeholder="Digite sua senha"
                    name="password"
                    autoComplete="current-password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-senha"
                    onClick={() => setMostrarSenha((v) => !v)}
                    aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                    disabled={loading}
                  >
                    {mostrarSenha ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </label>

              {usuarioAtual && (
                <div className="login-user-preview">
                  <div>
                    <strong>{usuarioAtual.nome}</strong>
                    <span>Código: {usuarioAtual.codFunc}</span>
                  </div>
                  <span className="login-user-badge">{usuarioAtual.nivel || "USER"}</span>
                </div>
              )}

              <button
                type="submit"
                className="login-submit"
                disabled={loading || loadingUsuarios}
              >
                {loading ? "Validando acesso..." : "Entrar no sistema"}
              </button>
            </form>
          </div>

          <div className="login-trust-row">
            <span>
              <FiShield />
              Sessão protegida
            </span>
            <span>
              <FiZap />
              Painel em tempo quase real
            </span>
          </div>
        </section>

        <aside className="login-command-panel">
          <div className="login-command-content">
            <div className="login-command-hero">
              <div>
                <span className="login-command-badge">
                  <FiServer /> SantSystem Command Center
                </span>
                <h2>Uma visão viva da operação, antes mesmo do login.</h2>
                <p>
                  Indicadores reais de suporte para reforçar controle, ritmo e
                  previsibilidade operacional.
                </p>
              </div>

              <div className="login-command-pulse">
                <i />
                {statusDashboard}
              </div>
            </div>

            <div className={`login-command-dashboard ${loadingDashboard ? "is-loading" : ""}`}>
              <div className="login-command-main-row">
                <div className="login-command-score">
                  <div
                    className="login-command-ring"
                    style={{
                      background: `conic-gradient(#22c55e ${taxaFinalizacaoHoje}%, rgba(148, 163, 184, 0.18) 0)`,
                    }}
                  >
                    <div>
                      <strong>{taxaFinalizacaoHoje}%</strong>
                      <span>finalização</span>
                    </div>
                  </div>

                  <div className="login-command-score-copy">
                    <span>Performance do dia</span>
                    <strong>{formatarNumero(dashboardLogin.kpis.chamadosHoje)} chamados</strong>
                    <small>
                      {formatarNumero(dashboardLogin.kpis.chamadosFinalizadosHoje)} finalizados ·{" "}
                      {formatarNumero(dashboardLogin.kpis.chamadosEmAtendimentoHoje)} em atendimento
                    </small>
                  </div>
                </div>

                <div className="login-command-stack">
                  <MiniMetric
                    icon={<FiUsers />}
                    label="Clientes ativos"
                    value={dashboardLogin.kpis.clientesAtivos}
                    detail="base operacional"
                    tone="green"
                  />
                  <MiniMetric
                    icon={<FiAlertTriangle />}
                    label="Projetos atrasados"
                    value={dashboardLogin.kpis.projetosAtrasados}
                    detail="prazo estourado"
                    tone="red"
                  />
                </div>
              </div>

              <div className="login-command-metrics">
                <MiniMetric
                  icon={<FiActivity />}
                  label="Chamados hoje"
                  value={dashboardLogin.kpis.chamadosHoje}
                  detail="demanda diária"
                  tone="blue"
                />
                <MiniMetric
                  icon={<FiCheckCircle />}
                  label="Finalizados"
                  value={dashboardLogin.kpis.chamadosFinalizadosHoje}
                  detail="concluídos hoje"
                  tone="cyan"
                />
                <MiniMetric
                  icon={<FiBriefcase />}
                  label="Projetos ativos"
                  value={dashboardLogin.kpis.projetosAtivos}
                  detail="delivery aberto"
                  tone="purple"
                />
                <MiniMetric
                  icon={<FiRadio />}
                  label="Releases em teste"
                  value={dashboardLogin.kpis.releasesEmTeste}
                  detail="validação interna"
                  tone="yellow"
                />
              </div>

              <div className="login-command-chart">
                <div className="login-command-chart-head">
                  <div>
                    <span>Evolução operacional</span>
                    <strong>Chamados nos últimos 7 dias</strong>
                  </div>
                  <FiTrendingUp />
                </div>

                <div className="login-command-bars">
                  {timelineDashboard.map((item) => (
                    <div className="login-command-bar-item" key={item.bucket || item.label}>
                      <span className="login-command-bar-value">
                        {item.total > 0 ? formatarNumero(item.total) : ""}
                      </span>

                      <div className="login-command-bar-track">
                        <span
                          className="login-command-bar-fill"
                          style={{ height: `${item.percentual}%` }}
                        />
                        <span
                          className="login-command-bar-resolved"
                          style={{ height: `${Math.max(0, item.resolvidosPercentual)}%` }}
                        />
                      </div>

                      <small>{item.label}</small>
                    </div>
                  ))}
                </div>
              </div>

              <div className="login-command-footer">
                <div>
                  <FiClock />
                  <span>Fila</span>
                  <strong>{formatarNumero(dashboardLogin.kpis.chamadosEmFilaHoje)}</strong>
                </div>
                <div>
                  <FiActivity />
                  <span>Atendimento</span>
                  <strong>{formatarNumero(dashboardLogin.kpis.chamadosEmAtendimentoHoje)}</strong>
                </div>
                <div>
                  <FiBarChart2 />
                  <span>Operação</span>
                  <strong>{dashboardErro ? "Fallback" : "Online"}</strong>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default LoginScreen;
